import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { getLocalCalendarDay, getDayDiff } from '../utils/dateUtils';

/**
 * useStreakFreeze Hook
 * 
 * Manages streak freeze functionality:
 * - Auto-replenishes 1 freeze per week (max 2)
 * - Automatically uses freeze when streak would break
 * - Syncs freeze count with database
 */
export const useStreakFreeze = (userSettings, setUserSettings) => {
    const { user } = useAuth();
    const [freezeCount, setFreezeCount] = useState(userSettings?.streakFreezes ?? 1);
    const [lastEarned, setLastEarned] = useState(userSettings?.streakFreezeLastEarned ?? null);
    const [lastUsed, setLastUsed] = useState(userSettings?.streakFreezeUsedDate ?? null);

    // Sync internal state with external userSettings
    useEffect(() => {
        if (userSettings?.streakFreezes !== undefined && userSettings.streakFreezes !== freezeCount) {
            setFreezeCount(userSettings.streakFreezes);
        }
        if (userSettings?.streakFreezeLastEarned && userSettings.streakFreezeLastEarned !== lastEarned) {
            setLastEarned(userSettings.streakFreezeLastEarned);
        }
        if (userSettings?.streakFreezeUsedDate && userSettings.streakFreezeUsedDate !== lastUsed) {
            setLastUsed(userSettings.streakFreezeUsedDate);
        }
    }, [userSettings?.streakFreezes, userSettings?.streakFreezeLastEarned, userSettings?.streakFreezeUsedDate]);

    // Check if user should earn a new freeze (weekly replenishment)
    useEffect(() => {
        if (!user || !lastEarned) return;

        const checkAndReplenish = () => {
            const now = new Date();
            const lastEarnedDate = new Date(lastEarned);
            const daysSinceLastEarned = Math.floor((now - lastEarnedDate) / (1000 * 60 * 60 * 24));

            // Award 1 freeze per week, max 2 total
            if (daysSinceLastEarned >= 7 && freezeCount < 2) {
                const freezesToAward = Math.min(Math.floor(daysSinceLastEarned / 7), 2 - freezeCount);
                if (freezesToAward > 0) {
                    const newFreezeCount = Math.min(freezeCount + freezesToAward, 2);
                    updateFreezeCount(newFreezeCount, now.toISOString());
                }
            }
        };

        checkAndReplenish();
    }, [user, lastEarned, freezeCount]);

    // Update freeze count in both local state and database
    const updateFreezeCount = useCallback(async (newCount, newLastEarned = null) => {
        setFreezeCount(newCount);

        if (newLastEarned) {
            setLastEarned(newLastEarned);
        }

        // Update local settings
        if (setUserSettings) {
            setUserSettings(prev => ({
                ...prev,
                streakFreezes: newCount,
                ...(newLastEarned && { streakFreezeLastEarned: newLastEarned })
            }));
        }

        // Sync to database
        if (user) {
            try {
                const updateData = {
                    streak_freezes: newCount
                };

                if (newLastEarned) {
                    updateData.streak_freeze_last_earned = newLastEarned;
                }

                await supabase
                    .from('profiles')
                    .update(updateData)
                    .eq('id', user.id);

                console.log('Streak freezes updated:', newCount);
            } catch (error) {
                console.error('Error updating freeze count:', error);
            }
        }
    }, [user, setUserSettings]);

    // Use one or more freezes to protect the streak
    const useFreeze = useCallback(async (count = 1) => {
        if (freezeCount < count) {
            console.warn('Not enough freezes available to use');
            return false;
        }

        const now = new Date().toISOString();
        const newCount = freezeCount - count;
        setFreezeCount(newCount);
        setLastUsed(now);

        // Update settings
        if (setUserSettings) {
            setUserSettings(prev => ({
                ...prev,
                streakFreezes: newCount,
                streakFreezeUsedDate: now
            }));
        }

        // Sync to database
        if (user) {
            try {
                await supabase
                    .from('profiles')
                    .update({
                        streak_freezes: newCount,
                        streak_freeze_used_date: now
                    })
                    .eq('id', user.id);

                console.log(`Used ${count} streak freeze(s)!`);
                return true;
            } catch (error) {
                console.error('Error using freeze:', error);
                return false;
            }
        }

        return true;
    }, [freezeCount, user, setUserSettings]);

    // Check if streak should be protected and how many freezes it costs
    const shouldProtectStreak = useCallback((lastPlayedDate) => {
        if (!lastPlayedDate) return { shouldProtect: false, requiredFreezes: 0 };

        const today = getLocalCalendarDay();
        const diffDays = getDayDiff(lastPlayedDate, today);

        // If played today or yesterday, streak is fine
        if (diffDays <= 1) {
            return { shouldProtect: false, requiredFreezes: 0 };
        }

        // Freezes needed = diffDays - 1 (e.g. diffDays=2 means missed 1 day -> 1 freeze)
        const requiredFreezes = diffDays - 1;

        // We can protect if we have enough freezes
        // and haven't already used freezes for this specific gap today
        const usedToday = lastUsed && lastUsed.split('T')[0] === today;
        const canProtect = !usedToday && freezeCount >= requiredFreezes;

        return {
            shouldProtect: canProtect,
            requiredFreezes: requiredFreezes,
            isBroken: diffDays > (freezeCount + 1) // Too many days missed for available freezes
        };
    }, [freezeCount, lastUsed]);

    return {
        freezeCount,
        lastEarned,
        lastUsed,
        useFreeze,
        shouldProtectStreak,
        updateFreezeCount,
        hasFreeze: freezeCount > 0
    };
};
